/**
 * searchConfidence: pure helpers that read the search_knowledge confidence
 * signal out of a tool_result entry's output.
 *
 * The intelligence runtime returns `confidence: high | low | none | filtered
 * | lexical | degraded` on search_knowledge results. `degraded` means the
 * rerank call failed and the results are in retrieval order; the tool still
 * reports success. `none` means the runtime found no confident answer.
 *
 * What the UI receives as `output` is the text the mission runner fed back to
 * the model: an optional governance label
 * `[GOVERNANCE trust=derived confidence=none]` followed by the evidence wrapper
 * `[RETRIEVED EVIDENCE ...]\n{json}\n[END EVIDENCE]`, which is why a plain
 * JSON.parse of the output fails. It may also arrive as a parsed object or a
 * bare JSON string. No known transport truncates the output; the regex
 * fallback keeps the badge if a string is ever cut short, because the
 * governance label and the `confidence` key (the first key of the JSON
 * object) sit at the start.
 */

export type SearchConfidenceState = 'degraded' | 'none' | null;

/** Tool names whose results carry the confidence signal. */
export function isKnowledgeSearchTool(toolName: string | null | undefined): boolean {
  return toolName === 'search_knowledge';
}

interface ParsedSearchOutput {
  confidence?: unknown;
  no_confident_answer?: unknown;
  guidance?: unknown;
  retrieval_diags?: unknown;
}

const RERANK_DIAG_PREFIX = 'rerank degraded: ';

// A knowledge result as the runner feeds it back: governance label first, or
// the evidence wrapper, or a bare JSON object.
const WRAPPED_RESULT_RE = /^\s*\[(GOVERNANCE|RETRIEVED EVIDENCE)\b/;
const BARE_OBJECT_RE = /^\s*\{/;
const GOVERNANCE_LABEL_CONFIDENCE_RE = /^\s*\[GOVERNANCE [^\]]*\bconfidence=([a-z]+)\]/;
const CONFIDENCE_FIELD_RE = /"confidence"\s*:\s*"([a-z]+)"/;
const RERANK_DIAG_FIELD_RE = /"retrieval_diags"\s*:\s*\[[^\]]*?"(rerank degraded: [^"]*)"/;

function parseJsonObject(text: string): ParsedSearchOutput | null {
  try {
    const parsed = JSON.parse(text) as unknown;
    return parsed != null && typeof parsed === 'object' ? (parsed as ParsedSearchOutput) : null;
  } catch {
    return null;
  }
}

function looksLikeKnowledgeResult(text: string): boolean {
  return WRAPPED_RESULT_RE.test(text) || BARE_OBJECT_RE.test(text);
}

function parseSearchOutput(output: unknown): ParsedSearchOutput | null {
  if (output != null && typeof output === 'object') {
    return output as ParsedSearchOutput;
  }
  if (typeof output !== 'string') return null;
  const direct = parseJsonObject(output);
  if (direct) return direct;
  // Only a wrapped result is unwrapped. An arbitrary error string with a
  // nested "{...}" is left alone so it cannot masquerade as a search result.
  if (!WRAPPED_RESULT_RE.test(output)) return null;
  const start = output.indexOf('{');
  const end = output.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  return parseJsonObject(output.slice(start, end + 1));
}

function toState(confidence: unknown): SearchConfidenceState {
  if (confidence === 'degraded') return 'degraded';
  if (confidence === 'none') return 'none';
  return null;
}

/**
 * Classifies the search_knowledge confidence signal carried in a tool
 * result's output. Returns 'degraded' when the reranker call failed (results
 * are in retrieval order), 'none' when the runtime found no confident answer,
 * or null when neither applies. Pass the entry's toolName to restrict the
 * read to knowledge-search results; without it every output is inspected.
 */
export function searchConfidenceState(output: unknown, toolName?: string | null): SearchConfidenceState {
  if (toolName !== undefined && !isKnowledgeSearchTool(toolName)) return null;
  const parsed = parseSearchOutput(output);
  if (parsed) return toState(parsed.confidence);
  if (typeof output !== 'string') return null;
  // String cut short: the governance label at the start, else the first
  // "confidence" key of a wrapped or bare JSON object.
  const label = GOVERNANCE_LABEL_CONFIDENCE_RE.exec(output);
  if (label) return toState(label[1]);
  if (!looksLikeKnowledgeResult(output)) return null;
  const field = CONFIDENCE_FIELD_RE.exec(output);
  return field ? toState(field[1]) : null;
}

/**
 * Extracts a one-line reason for a degraded search: the retrieval_diags entry
 * that explains the rerank failure when present, else the runtime's guidance
 * string, else null.
 */
export function searchDegradedReason(output: unknown, toolName?: string | null): string | null {
  if (toolName !== undefined && !isKnowledgeSearchTool(toolName)) return null;
  const parsed = parseSearchOutput(output);
  if (parsed) {
    const diags = Array.isArray(parsed.retrieval_diags) ? (parsed.retrieval_diags as unknown[]) : [];
    const diag = diags.find((d): d is string => typeof d === 'string' && d.startsWith(RERANK_DIAG_PREFIX));
    if (diag) return diag;
    if (typeof parsed.guidance === 'string' && parsed.guidance) return parsed.guidance;
    return null;
  }
  if (typeof output !== 'string' || !looksLikeKnowledgeResult(output)) return null;
  return RERANK_DIAG_FIELD_RE.exec(output)?.[1] ?? null;
}
